import { Segment } from '@dna/analytics'
import { client } from '@dna/constructor'
import { RespondToViewport, WithProfiler } from '@dna/mixins'
import { closeSmall, filter } from '@dna/paths'
import styles from '@dna/styles'
import { getSectionPosition, t } from '@dna/utils'
import {
  LitElement,
  classMap,
  createRef,
  css,
  html,
  nothing,
  ref,
  when,
} from '@lit'

try {
  window?.performance?.measure('component-constructor-results', {
    start: 'rothys.script.component-constructor-results.start',
    detail: {
      devtools: {
        trackGroup: 'Rothys',
        track: 'Script Loading',
      },
    },
  })
} catch (e) {
  console.error(e)
}

const TaskStatus = {
  PENDING: 'PENDING',
  LOADING: 'LOADING',
  COMPLETE: 'COMPLETE',
  ERROR: 'ERROR',
}

const cleanedSearchQuery = (query) => {
  if (!query) return ''
  // Remove slashes
  return query.replace(/[\/\\]/g, '')
}

/**
 * @element dna-constructor-results
 * @slot product-card-placeholder - Placeholder for product card
 *
 * This component is used to display the results of a search or collections page.
 * Can also be used to just show a simple collection / grid of products.
 */

class DNAConstructorResults extends WithProfiler(
  RespondToViewport(LitElement),
) {
  static properties = {
    searchTerm: {
      type: String,
    },
    // Defaults to Browse
    isSearch: {
      type: Boolean,
      attribute: 'is-search',
    },
    collectionHandle: {
      type: String,
      attribute: 'collection-handle',
    },
    showFilters: {
      type: Boolean,
      attribute: 'show-filters',
    },
    showPagination: {
      type: Boolean,
      attribute: 'show-pagination',
    },
    showSort: {
      type: Boolean,
      attribute: 'show-sort',
    },
    resultsPerPage: {
      type: Number,
      attribute: 'results-per-page',
    },
    showCardCarousels: {
      type: Boolean,
      attribute: 'show-card-carousels',
      reflect: true,
    },
    enableHoverImage: {
      type: Boolean,
      attribute: 'enable-hover-image',
      reflect: true,
    },
    quickView: {
      type: Boolean,
      attribute: 'quick-view',
    },
    filtersOpen: {
      type: Boolean,
      attribute: 'filters-open',
      reflect: true,
    },
    inGrids: {
      type: Array,
    },
    status: {
      type: String,
    },
    startedInitialLoad: {
      type: Boolean,
    },
    sectionID: {
      type: String,
      attribute: 'section-id',
    },
    fitTips: {
      type: Array,
    },
    hidePriceControlOptions: {
      type: Boolean,
    },
    hasRenderedSchema: {
      type: Boolean,
    },
    allProducts: {
      type: Array,
      state: true,
    },
    hideProductColorCount: {
      type: Boolean,
      attribute: 'hide-product-color-count',
      reflect: true,
    },
    showEditorialAltImage: {
      type: Boolean,
      attribute: 'show-editorial-alt-image',
      reflect: true,
    },
    hideProductColorName: {
      type: Boolean,
      attribute: 'hide-product-color-name',
      reflect: true,
    },
    experimentationTrackingKey: {
      type: String,
      attribute: 'experimentation-tracking-key',
    },
    trackingKey: {
      type: String,
      attribute: 'tracking-key',
    },
    exposeFilters: {
      type: Boolean,
      attribute: 'expose-filters',
      reflect: true,
    },
    showReviewStars: {
      type: Boolean,
      attribute: 'show-review-stars',
    },
  }

  constructor() {
    super()

    performance.mark('component-constructor-results-start')
    this.router = __DNA_GLOBAL__.utils.queryRouter
    this._setMobileBreakpoints(['mobile', 'tablet'])

    // get from query
    const query = this.router.query.get()
    this.searchTerm =
      cleanedSearchQuery(query?.q?.[0]) ??
      __DNA_GLOBAL__?.constructor?.searchTerm

    this.showFilters = false
    this.showPagination = false
    this.showSort = false
    this.resultsPerPage = __DNA_GLOBAL__?.constructor?.resultsPerPage
    this.searchResultsPerPage =
      __DNA_GLOBAL__?.constructor?.searchResultsPerPage
    this.isSearch = __DNA_GLOBAL__?.constructor?.isSearch ?? false
    this.collectionHandle = __DNA_GLOBAL__?.constructor?.collectionHandle ?? ''
    this.inGrids = []
    this.results = null
    this.status = TaskStatus.PENDING
    this.filtersOpen = false
    this.topBarRef = createRef()
    this.startedInitialLoad = false
    this.cartItems = []
    this.sectionID = ''
    this.fitTips = []
    this.hidePriceControlOptions = false
    this.hasRenderedSchema = false
    this.allProducts = []
    this.rafID = null
    this.showMoreClicked = false
    this.queryParams = this.router.query.get()
    this.sortTriggered = false
    this.filterBarRef = createRef()

    const params = this.router.query.getNormalized()
    const pageNum = Array.isArray(params.page)
      ? Number.parseInt(params.page[0])
      : Number.parseInt(params.page)

    this.currentPage = pageNum ? pageNum : 1

    if (this.collectionHandle || this.isSearch) {
      this.loadInitialResults()
    }
  }

  connectedCallback() {
    super.connectedCallback()

    this.$filters = this.querySelector('dna-grid-filters')
    this.$section = this.closest('.shopify-section')
    this.sectionPosition = getSectionPosition(this.$section)

    window.addEventListener('scroll', this._handleScroll, { passive: true })
    if (this.currentPage > 1 && history.scrollRestoration) {
      history.scrollRestoration = 'manual'
    }

    this.io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          this.handleObserverEntry(entry)
        }
      },
      {
        root: null,
        rootMargin: '0px',
        threshold: 0.5,
      },
    )
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    window.removeEventListener('scroll', this._handleScroll)
  }

  async showMoreRef(ref) {
    if (ref) {
      await ref.updateComplete
      this.io.observe(ref)
    } else {
      this.io.disconnect()
    }
  }

  handleObserverEntry(entry) {
    if (entry.isIntersecting) {
      this.prefetchProductsObserver()
    }
  }

  async prefetchProductsObserver() {
    if (!(this.prefetchedResults instanceof Promise)) {
      await this.prefetchProducts()
    }
  }

  _handleViewportChange(newViewport) {
    super._handleViewportChange(newViewport)
    this.requestUpdate()
  }

  _handleScroll = () => {
    if (this.rafID === null) {
      this.rafID = requestAnimationFrame(() => {
        this.rafID = null
        if (this.currentPage > 1 && this.status === TaskStatus.COMPLETE) {
          sessionStorage.setItem('rothys_scroll_position', window.scrollY)
        }
      })
    }
  }

  firstUpdated() {
    super.firstUpdated()

    if (!this.startedInitialLoad) {
      this.loadInitialResults()
    }

    this.router?.events?.addEventListener('queryChange', (event) => {
      const updatedParams = this.router.query.get()
      const sortEqual = this.queryParams?.sort?.[0] === updatedParams?.sort?.[0]
      const sortByEqual =
        this.queryParams?.sort_order?.[0] === updatedParams?.sort_order?.[0]
      this.sortTriggered = !(sortEqual && sortByEqual)

      if (!this.showMoreClicked) {
        if (!this.sortTriggered) {
          this.allProducts = []
        }
        this.prefetchedResults = null
        this.currentPage = 1
      }
      this.fetchProducts()
      this.showMoreClicked = false
      this.queryParams = updatedParams
    })

    document.addEventListener('DOMContentLoaded', () => {
      // We have event listeners listening for events only after the DOMContentLoaded
      // This is to ensure that the event is fired after event listeners start listening
      // Issue is apparent in the search results page without this code block
      setTimeout(() => {
        if (this.status === TaskStatus.COMPLETE) {
          this.dispatchEvent(
            new CustomEvent('dna:constructor-results:loaded', {
              detail: this.results,
              bubbles: true,
            }),
          )
        }
      }, 0)
    })

    document
      .querySelector(':root')
      ?.style.setProperty(
        '--dna-filter-bar-height',
        this.filterBarRef?.value
          ? `${this.filterBarRef.value.getBoundingClientRect().height}px`
          : '0px',
      )
  }

  updateResults(results) {
    this.results = results
    // We want to replace the products in the array when sorting
    if (this.sortTriggered) {
      this.allProducts = results.products
    } else {
      this.allProducts.push(...results.products)
    }

    this.status = TaskStatus.COMPLETE

    this.dispatchEvent(
      new CustomEvent('dna:constructor-results:loaded', {
        detail: results,
        bubbles: true,
      }),
    )
    performance.mark('component-constructor-results-end')
    try {
      performance.measure('component-constructor-results', {
        start: 'component-constructor-results-start',
        end: 'component-constructor-results-end',
        detail: {
          devtools: {
            track: 'Constructor',
            trackGroup: 'Rothys',
            properties: [['Collection Handle', this.collectionHandle]],
          },
        },
      })
    } catch (e) {
      console.error(e)
    }
  }

  async loadInitialResults() {
    this.startedInitialLoad = true
    // Check if prefetch promise is available
    if (__DNA_GLOBAL__?.constructor?.prefetchPromise instanceof Promise) {
      console.log('CIO Prefetch promise was found')
      this.dispatchEvent(new CustomEvent('dna:load:start'))
      const result = await __DNA_GLOBAL__.constructor?.prefetchPromise
      this.updateResults(result)
    } else {
      console.log('CIO Prefetch promise was not found')
      this.fetchProducts(true)
    }
  }

  async prefetchProducts() {
    try {
      const response = this.isSearch
        ? await this.searchQuery(true)
        : await this.browseQuery(true)
      this.prefetchedResults = Promise.resolve(response)
    } catch (err) {
      console.error('Error preloading results:', err)
    }
  }

  toggleFilters() {
    let bucketFilterIsOpen = false
    const $filters = this.renderRoot.querySelector(
      'dna-grid-filters[expose-filters]',
    )
    if ($filters) {
      const $exposedFilters = $filters.renderRoot
        ?.querySelector('.filter-group--buckets')
        ?.querySelectorAll('sl-dropdown')
      if ($exposedFilters?.length) {
        const $exposedFiltersArr = Array.from($exposedFilters)
        bucketFilterIsOpen = $exposedFiltersArr.some(($exposedFilter) => {
          return $exposedFilter.open || false
        })
      }
    }

    if (!bucketFilterIsOpen) {
      this.filtersOpen = !this.filtersOpen
      if (this.filtersOpen) {
        if ($exposedFilters?.length) {
          for (const $filter of $exposedFilters) {
            $filter.hide()
          }
        }
        this.$filters?.focus()
        this.trackFiltersViewed()
      } else {
        this?.focus()
      }
    } else {
      if (this.filtersOpen) {
        this.filtersOpen = !this.filtersOpen
      }
    }
  }

  async searchQuery(isPrefetching = false, isInitialLoad = false) {
    const params = this.router.query.getNormalized()
    if (isInitialLoad) {
      const fetchPromises = []
      for (let page = 1; page <= this.currentPage; page++) {
        const pageParams = { ...params, page }
        fetchPromises.push(client.getSearchResults(this.searchTerm, pageParams))
      }
      const allResults = await Promise.all(fetchPromises)
      return {
        ...allResults[0],
        products: allResults.flatMap((result) => result.products),
      }
    }
    params.page = isPrefetching
      ? this.currentPage + 1
      : params.page || this.currentPage

    const response = await client.getSearchResults(this.searchTerm, {
      ...params,
      resultsPerPage: this.searchResultsPerPage,
    })

    return response
  }

  async browseQuery(isPrefetching = false, isInitialLoad = false) {
    const params = this.router.query.getNormalized()
    if (isInitialLoad) {
      const fetchPromises = []
      for (let page = 1; page <= this.currentPage; page++) {
        const pageParams = { ...params, page }
        fetchPromises.push(
          client.getBrowseProducts(this.collectionHandle, pageParams),
        )
      }
      const allResults = await Promise.all(fetchPromises)
      return {
        ...allResults[0],
        products: allResults.flatMap((result) => result.products),
      }
    }
    params.page = isPrefetching
      ? this.currentPage + 1
      : params.page || this.currentPage
    const response = await client.getBrowseProducts(this.collectionHandle, {
      ...params,
      resultsPerPage: this.resultsPerPage,
    })
    return response
  }

  fetchProducts = async (isInitialProducts = false) => {
    this.status = TaskStatus.LOADING
    try {
      let response
      let shouldScrollToTop
      if (this.prefetchedResults instanceof Promise) {
        response = await this.prefetchedResults
        this.prefetchedResults = null
      } else {
        this.dispatchEvent(new CustomEvent('dna:load:start'))

        response = this.isSearch
          ? await this.searchQuery(false, isInitialProducts)
          : await this.browseQuery(false, isInitialProducts)
        shouldScrollToTop = true
      }
      this.updateResults(response)
      this.prefetchedResults = null

      if (this.topBarRef.value && shouldScrollToTop) {
        this.topBarRef.value.scrollIntoView({ behavior: 'smooth' })
      }
    } catch (err) {
      console.error('Error fetching results:', err)
      this.status = TaskStatus.ERROR
      throw new Error(response)
    }
  }

  clearAllFilters() {
    const queryData = this.router.query.get()
    const filters = Object.keys(queryData).filter((key) =>
      key.startsWith('filter.'),
    )

    const filtersObj = filters.reduce((acc, key) => {
      acc[key] = null
      return acc
    }, {})

    this.router.query.update(filtersObj)

    this.trackFiltersCleared()
  }

  get products() {
    return this.allProducts || []
  }

  get totalCount() {
    return this.results?.meta?.totalCount || undefined
  }

  get filters() {
    return this.results?.filters || []
  }

  get activeFilters() {
    return this.filters.map((filter) => {
      return {
        ...filter,
        options: undefined,
        selectedOptions: Array.isArray(filter?.options)
          ? filter.options.filter((option) => option.status === 'selected')
          : [],
      }
    })
  }

  get activeSelectedFilters() {
    return this.activeFilters.filter(
      (filter) => filter.selectedOptions.length > 0,
    )
  }

  get sort() {
    if (!this.results?.sort) {
      return {
        options: [],
        selected: '',
      }
    }
    const selected = this.results?.sort?.selected
    return {
      options: this.results?.sort?.options || [],
      selected: `${selected.sort_by}|${selected.sort_order}`,
    }
  }

  get totalResults() {
    return this.results?.pagination?.totalResults
  }

  get selectedOptionsValues() {
    return this.activeFilters.flatMap((filter) => filter.selectedOptions)
  }

  get mobileFilterButtonText() {
    if (this.totalCount === 0) {
      return 'See Products'
    }

    return `See ${this.totalCount} Product${this.totalCount > 1 ? 's' : ''}`
  }

  get searchandising() {
    return this.results?.searchandising
  }

  showMore() {
    this.showMoreScrollPosition = window.scrollY
    this.showMoreClicked = true
    this.router.query.update({
      page: this.currentPage + 1,
    })
    this.currentPage = this.currentPage + 1
  }

  removeFilterOption(filter, option) {
    this.router.query.remove({
      [`filter.${filter?.name}`]: option.value,
    })
    this.trackFilterRemoved({
      filter_name: option?.name?.toLowerCase(),
      filter_type: filter?.display_name?.toLowerCase(),
    })
  }

  trackFiltersViewed() {
    // @segment
    Segment.protect(() => {
      Segment.track(Segment.CONSTANTS.EVENTS.FILTERS_VIEWED)
    })
  }

  trackFiltersCleared() {
    // @segment
    Segment.protect(() => {
      Segment.track(Segment.CONSTANTS.EVENTS.FILTERS_CLEARED)
    })
  }

  trackFilterRemoved(filterData) {
    // @segment
    Segment.protect(() => {
      Segment.track(Segment.CONSTANTS.EVENTS.FILTER_REMOVED, filterData)
    })
  }

  _renderSchema() {
    if (this.products.length > 0 && !this.hasRenderedSchema) {
      const scriptContent = {
        '@context': 'http://schema.org/',
        '@type': 'ItemList',
        itemListElement: this.products.map((product, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          item: {
            '@type': 'Product',
            name: product.title,
            url: `${window.location.origin}${Shopify.routes.root}products/${product.handle}`,
            image: product.media
              .filter((media) => media.type === 'image')
              .slice(0, 3)
              .map((media) => media.url),
            ...(product.metafields?.yotpo?.reviews_average > 0 &&
            product.metafields?.yotpo?.reviews_count > 0 &&
            !product.tags.includes('YBlocklist')
              ? {
                  aggregateRating: {
                    '@type': 'AggregateRating',
                    ratingValue: product.metafields?.yotpo?.reviews_average,
                    reviewCount: product.metafields?.yotpo?.reviews_count,
                  },
                }
              : {}),
            offers: {
              '@type': 'Offer',
              price: product.prices[Shopify.country],
              priceCurrency: window.Shopify.currency.active,
              ...(product.compareAtPrices[Shopify.country] &&
              product.compareAtPrices[Shopify.country] >
                product.prices[Shopify.country]
                ? {
                    priceSpecification: {
                      '@type': 'UnitPriceSpecification',
                      priceType: 'https://schema.org/ListPrice',
                      price: product.compareAtPrices[Shopify.country],
                      priceCurrency: window.Shopify.currency.active,
                    },
                  }
                : {}),
            },
          },
        })),
      }
      const scriptElement = document.createElement('script')
      scriptElement.type = 'application/ld+json'
      scriptElement.textContent = JSON.stringify(scriptContent)
      document.head.appendChild(scriptElement)
      this.hasRenderedSchema = true
    }
  }

  _renderPagination = () => {
    const currentTotalAmount = this.products.length
    const totalResults = this.totalResults
    const updatedProgress = (currentTotalAmount / totalResults) * 100
    return html`
      <div class="pagination">
        <div class="pagination__count">${currentTotalAmount} of ${totalResults}</div>
        <div class="pagination__progress-bar">
          <div class="pagination__progress-bar--content" style="width:${updatedProgress}%"></div>
        </div>
        ${when(
          currentTotalAmount < totalResults,
          () => html`
            <sl-button ${ref(this.showMoreRef)} class="pagination__button" variant="primary" @click=${this.showMore}>
              ${t('sections.collection_template.show_more_button')}
            </sl-button>
          `,
          () => nothing,
        )}
      </div>
    `
  }

  _renderFilterDrawer = () => html`
    <sl-drawer
      placement="start"
      class="mobile-plp-filter"
      .open=${this.filtersOpen}
      @sl-request-close=${() => {
        // Ensure open flag is updated when closed via button or overlay click
        this.filtersOpen = false
      }}
    >
      ${when(
        this.showSort && this.exposeFilters,
        () => html`
          <h3 class="h3" slot="label">${t('sections.collection_template.filter_and_sort_html')}</h3>
          <dna-grid-sort
            ?expose-filters=${this.exposeFilters}
            .sort=${this.sort}
            .hidePriceControlOptions=${this.hidePriceControlOptions}
          >
            <div class="skeleton skeleton--heading mt-6 mb-4"></div>
          </dna-grid-sort>
        `,
        () => html`
          <h3 class="h6" slot="label">${t('sections.collection_template.filters')}</h3>
        `,
      )}
      <dna-grid-filters
        data-drawer
        .filters=${this.filters}
        .hidePriceControlOptions=${this.hidePriceControlOptions}
      >
        <div slot="loading">
          <p>Loading...</p>
        </div>
      </dna-grid-filters>

      <div slot="footer" class="mobile-plp-filter__footer">
        <button
          class="label-s clear-filters-btn uppercase"
          @click=${this.clearAllFilters}
        >
          ${t('sections.collection_template.clear.multiple')}
        </button>
        <sl-button
          class="label-s see-products-btn"
          @click=${() => {
            this.filtersOpen = false
          }}
          variant='primary'
        >
          ${this.mobileFilterButtonText}
        </sl-button>
      </div>
    </sl-drawer>
  `

  _renderActiveFilters = () => {
    return html`
      <div class="filter-tags">
        ${this.activeFilters.map((filter) => {
          return filter.selectedOptions.map((option) => {
            return html`<span class="filter-tag label-s uppercase"
              data-filter=${filter.display_name}>
                ${option.name}
                <button class="remove-btn"
                  aria-label="${t('sections.collection_template.clear.single')} ${
                    filter.name
                  } ${option.value}"
                  @click=${() => this.removeFilterOption(filter, option)}
                >
                  ${closeSmall}
                </button>
              </span>`
          })
        })}
        <button
          class="clear-filters-top-btn"
          @click=${this.clearAllFilters}
        >
          ${when(
            this.exposeFilters,
            () => html`${t('sections.collection_template.clear.all')}`,
            () => html`${t('sections.collection_template.clear.multiple')}`,
          )}
        </button>
      </div>
    `
  }

  render() {
    const topBarMarkup = html`
      <div class="top-bar-scroll-reference" ${ref(this.topBarRef)}></div>
      <div
        class=${classMap({
          'constructor-results__top-bar': true,
        })}
        ${ref(this.filterBarRef)}
      >
        <div class="filter">
          ${when(
            this.showFilters && this.exposeFilters,
            () => html`
              <dna-grid-filters
                ?expose-filters=${this.exposeFilters}
                .filters=${this.filters}
                .hidePriceControlOptions=${this.hidePriceControlOptions}
                .productCount=${this.totalCount}
              >
                <div slot="loading">
                  <div class="skeleton skeleton--heading mt-6 mb-6"></div>
                  ${Array(6).fill(
                    html`<div class="skeleton skeleton--small-text mt-6 mb-6"></div>`,
                  )}
                </div>
              </dna-grid-filters>
            `,
            () => nothing,
          )}
          ${when(
            this.showFilters,
            () => html`
              <button
                class="flex flex-align-center filter-toggle js-filter-toggle"
                aria-expanded=${this.filtersOpen}
                @click=${this.toggleFilters}
              >
                ${when(
                  this.exposeFilters,
                  () => nothing,
                  () => html`${filter}`,
                )}
                <span
                  class="${classMap({
                    flex: this.exposeFilters,
                    'flex-align-center': this.exposeFilters,
                  })}"
                >
                  ${when(
                    this.isMobile && this.exposeFilters,
                    () => nothing,
                    () =>
                      html`
                        <span class="filter-toggle__text"> ${t('sections.collection_template.filter')}</span>
                      `,
                  )}
                  ${when(
                    this.exposeFilters,
                    () => html`${filter}`,
                    () => nothing,
                  )}
                  ${when(
                    this.totalCount,
                    () => html`
                      <span class="filter-toggle__count">
                        (${this.totalCount}${when(
                          this.isMobile,
                          () => nothing,
                          () =>
                            html` ${t('sections.collection_template.match')}`,
                        )})
                      </span>
                    `,
                    () => nothing,
                  )}
                </span>
              </button>
            `,
          )}
        </div>
        ${when(
          !this.exposeFilters && this.activeSelectedFilters.length > 0,
          this._renderActiveFilters,
          () => nothing,
        )}
        ${when(
          this.showSort,
          () =>
            when(
              this.isMobile && this.exposeFilters,
              () => nothing,
              () => html`
                <dna-grid-sort
                  ?expose-filters=${this.exposeFilters}
                  .sort=${this.sort}
                  .hidePriceControlOptions=${this.hidePriceControlOptions}
                >
                  <div class="skeleton skeleton--heading mt-6 mb-4"></div>
                </dna-grid-sort>
              `,
            ),
          () => nothing,
        )}
      </div>
      ${when(
        this.exposeFilters && this.activeSelectedFilters.length > 0,
        this._renderActiveFilters,
        () => nothing,
      )}
    `
    this._renderSchema()

    if (!(this.status === TaskStatus.COMPLETE && this.products.length === 0)) {
      return html`
        <div class="constructor-results flex flex-align-center">
          <div
            ?expose-filters=${this.exposeFilters}
            class=${classMap({
              'constructor-results__grid-container': true,
              'filter-active': this.filtersOpen,
            })}
          >
            ${this.showFilters || this.showSort ? topBarMarkup : nothing}
            <div class="flex flex-align-top results-container">
              <div class=${classMap({
                filters: true,
                active: this.filtersOpen,
              })}>
                <dna-grid-filters
                  .filters=${this.filters}
                  .hidePriceControlOptions=${this.hidePriceControlOptions}
                >
                  <div slot="loading">
                    <div class="skeleton skeleton--heading mt-6 mb-6"></div>
                    ${Array(6).fill(
                      html`<div class="skeleton skeleton--small-text mt-6 mb-6"></div>`,
                    )}
                  </div>
                </dna-grid-filters>
              </div>
              <dna-collection-grid
                .products=${this.products}
                .status=${this.status}
                .inGrids=${this.inGrids}
                .searchandising=${this.searchandising}
                .isSearch=${this.isSearch}
                .collectionHandle=${this.collectionHandle}
                .totalResults=${this.totalResults}
                .fitTips=${this.fitTips}
                ?filters-active=${this.filtersOpen}
                ?filters-applied=${this.activeSelectedFilters.length > 0}
                ?show-card-carousels=${this.showCardCarousels}
                ?enable-hover-image=${this.enableHoverImage}
                ?hide-product-color-count=${this.hideProductColorCount}
                ?hide-product-color-name=${this.hideProductColorName}
                ?show-editorial-alt-image=${this.showEditorialAltImage}
                ?show-review-stars=${this.showReviewStars}
                ?quick-view=${this.quickView}
                class=${classMap({
                  loading: this.status === TaskStatus.LOADING,
                })}
                section-id=${this.sectionID}
                section-position=${this.sectionPosition}
                experimentation-tracking-key=${this.experimentationTrackingKey}
                tracking-key=${this.trackingKey}
                .showMoreScrollPosition=${this.showMoreScrollPosition}
              >
                <div slot="loading">
                  <slot name="product-card-placeholder"></slot>
                </div>
              </dna-collection-grid>
            </div>
          </div>
        </div>
        ${when(this.showPagination, this._renderPagination, () => nothing)}
        ${when(
          this.isMobile,
          () => html`
            <div class="mobile-plp-filter-drawer">
              ${this._renderFilterDrawer()}
            </div>
          `,
          () => nothing,
        )}
      `
    }
    return nothing
  }

  static styles = [
    styles.reset,
    styles.flex,
    styles.container,
    styles.typography,
    styles.a11y,
    styles.shoelace,
    css`
      sl-drawer:not(:defined) {
        display: none;
      }
      
      .constructor-results {
        gap: var(--dna-space-4);
        flex-direction: column;
        position: relative;

        @media screen and (min-width: 1024px) {
          flex-direction: row;
        }
      }

      .constructor-results__grid-container {
        width: 100%;

        @media screen and (min-width: 1024px) {
          flex: 0 1 auto;
          width: 100%;
          display: flex;
          flex-direction: column;
        }
      }

      .filters {
        display: none;
      }
        
      .filters {
        top: calc(var(--dna-filter-bar-height) + var(--dna-header-height));
      }

      dna-collection-grid {
        flex:1;
      }

      .filter-toggle {
        font: var(--dna-body-p3-font);
        font-size: 13px;
        gap: 8px;
      }

      .filter-toggle:focus-visible {
        outline: 1px solid var(--dna-utility-focus);
        outline-offset: var(--dna-focus-outline-offset);
      }
        
      .filter-toggle__count { 
        color: var(--dna-utility-disabled);
      }

      @media screen and (min-width: 1024px) {
        .filter-toggle {
          font: var(--dna-body-p1-font);
          gap: 16px;
        }

        .filter-toggle__count {
          font: var(--dna-body-p1-font);
        }

        .filters dna-grid-filters {
          width: 100%;
          opacity: 0 ;
          visibility: hidden;
          position: relative;
          left: -100%;
          transition: all 0.3s;
        }

        .filters.active dna-grid-filters {
          left: 0;
          opacity: 1;
          visibility: visible;
        }

        .filters {
          align-self: flex-start;
          display: block;
          position: sticky;
          max-height: calc(100vh - var(--dna-filter-bar-height));
          overflow-y: auto;
          max-width: 0;
          transition: all 0.3s;
        }

        .filters {
          max-height: calc(100vh - (var(--dna-header-height) + var(--dna-filter-bar-height)));
        }

        .filters.active {
          max-width: 360px;
          width: 360px;
        }
      }

      .top-bar-scroll-reference {
        scroll-margin-top: var(--dna-header-height);
      }

      .constructor-results__top-bar {
        top: calc(var(--dna-header-height) - 1px);
      }

      .constructor-results__top-bar .filter {
        @media screen and (min-width: 1024px) {
          flex: 0 0 auto;
        }
      }
      
      .constructor-results__top-bar .filter-tags {
        order: 3;
        flex-basis: 100%;

        @media screen and (min-width: 1024px) {
          order: unset;
          flex-basis: auto;
          flex-wrap: wrap;
          margin: 0 var(--dna-space-10);
          padding-bottom: 0;
          scrollbar-width: thin;
        }
      }

      .constructor-results__top-bar {
        min-height: 68px;
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
        flex-direction: row;
        justify-content: space-between;
        align-items: center;
        background-color: var(--dna-color-sandstone);
        padding: 0 16px;
        position: sticky;
        z-index: var(--dna-z-index-overlay-base);
        transition: top .5s;

        @media screen and (min-width: 1024px) {
          align-items: center;
          flex-wrap: nowrap;
          padding: calc(var(--dna-space-6) - var(--dna-space-0-5)) var(--dna-space-10);
        }
      }
      .results-container {
        border-top: 1px solid var(--dna-brand-reverse);
      }

      .filter-tags {
        display: flex;
        flex: 1;
        gap: 8px;
        justify-content: flex-start;
        padding-bottom: var(--dna-space-4);
        overflow: auto;
        white-space: nowrap;
      }

      .filter-tag {
        background-color: var(--dna-brand-reverse);
        padding: 8px 10px;
        display: inline-flex;
        gap: 4px;
        align-items: center;
      }

      .remove-btn,
      .remove-btn svg {
        width: 16px;
        height: 16px;
      }

      .remove-btn:focus-visible {
        outline-style: solid;
        outline-color: var(--dna-utility-focus);
        outline-offset: var(--dna-focus-outline-offset);
      }

      .pagination sl-button[disabled],
      .pagination-btn[disabled] {
        pointer-events: none;
      }

      .pagination {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        margin: 20px auto;
      }
      .pagination__progress-bar {
        width: 260px;
        height: 3px;
        background-color: #D9D9D9;
        border-radius: 100px;
        margin-top: 12px;
        margin-bottom: 20px;
      }
      .pagination__progress-bar--content {
        background-color: #092DC5;
        border-radius: 100px;
        height: 3px;
      }

      .flip-icon svg {
        transform: scaleX(-1)
      }

      .mobile-plp-filter-drawer {
        position: relative;
        z-index: 2147483099;
      }

      sl-drawer::part(body) {
        padding: 0;
      }

      sl-drawer::part(header) {
        background-color: white;
        height: 70px;
        align-items: center;
        display: flex;
        padding: 0 var(--dna-container-horizontal-padding, 16px);
      }

      sl-drawer::part(title) {
        padding: 0;
      }

      sl-drawer::part(header-actions) {
        padding: 0;
      }

      sl-drawer::part(close-button__base) {
        color: black;
        width: 24px;
        height: 24px;
        font-size: 24px;
        padding: 0;
      }

      sl-drawer::part(footer) {
        border-top: 1px solid var(--dna-color-midnight);
        padding: 16px 16px;
      }

      sl-button::part(base) {
        padding: 16px 24px;
      }

      .mobile-plp-filter__footer {
        display: flex;
        justify-content: space-evenly;
        gap: 6px;
      }

      dna-grid-sort {
        z-index: var(--dna-z-index-dropdown, 300);
      }

      .clear-filters-btn {
        border: none;
        background: none;
        color: var(--dna-color-midnight);
      }

      .clear-filters-top-btn {
        border: none;
        background: none;
        color: var(--dna-color-midnight);
        text-decoration: underline;
        font-size: 12px;
        font-weight: 400;
        line-height: 18px;
        letter-spacing: 0.5px;
        padding-left: calc(var(--dna-space-5) / 2);
        text-align: left;
        white-space: nowrap;
      }
      
      .clear-filters-top-btn:focus-visible {
        outline: 1px solid var(--dna-utility-focus);
        outline-offset: var(--dna-focus-outline-offset);
      }

      .see-products-btn::part(label) {
        padding: 0;
      }

      @keyframes fade {
        0%, 100% { opacity: 0.2; }
        50% { opacity: 1; }
      }

      .loading {
        animation: fade 2s ease-in-out infinite;
      }

      [expose-filters] {
        .constructor-results__top-bar {
          min-height: 50px;
          padding: var(--dna-space-4) var(--dna-space-4);

          @media screen and (min-width: 1024px) {
            min-height: unset;
            padding: calc(var(--dna-space-4) - var(--dna-space-0-5)) var(--dna-space-10);
          }
        }
        dna-grid-sort {
          @media screen and (min-width: 1024px) {
            margin-left: calc(var(--dna-space-20) * 2);
          }
        }
        .filter-toggle {
          gap: calc(var(--dna-space-3) / 2);
          flex-shrink: 0;
          order: -1;
          margin-right: calc(var(--dna-space-10) / 2);
          overflow: hidden;
          transition: gap .4s ease;

          @media screen and (min-width: 1024px) {
            gap: var(--dna-space-2);
            font: var(--dna-body-p1-font);
            margin-left: var(--dna-space-5);
            order: unset;
          }
        }
        .filter-toggle__text {
          display: inline-flex;
          overflow: hidden;
          padding-left: var(--dna-space-2);
          white-space: nowrap;
          transition: width .4s ease;

          @media screen and (min-width: 1024px) {
            padding-left: 0;
            padding-right: var(--dna-space-2);
          }
        }
        .filter-toggle__count {
          margin-left: calc(var(--dna-space-3) / 2);

          @media screen and (min-width: 1024px) {
            margin-left: 0;
            padding: 0 var(--dna-space-4);
          }
        }
        .filter {
          display: flex;
          align-items: center;
          width: 100%;

          @media screen and (min-width: 1024px) {
            display: flex;
            flex-wrap: wrap;
            width: unset;
          }
        }
        .filter-tags {
          background-color: var(--dna-color-sandstone);
          border-top: 1px solid var(--dna-brand-reverse);
          margin: 0;
          padding: var(--dna-space-4);

          @media screen and (min-width: 1024px) {
            flex: 1 0 100%;
            background-color: var(--dna-color-sandstone);
            border-top: 1px solid var(--dna-brand-reverse);
            margin: 0;
            padding: var(--dna-space-4) var(--dna-space-10);
          }
        }
      }
    `,
  ]
}

customElements.define('dna-constructor-results', DNAConstructorResults)
